import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPartner } from '../data/partners';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { trackEvent } from '../utils/analytics';
import type { Partner } from '../types/partners';
import { NotFound } from './NotFound';
import { PartnerProfileHero } from '../components/partners/profile/PartnerProfileHero';
import { PartnerContextSection } from '../components/partners/profile/PartnerContextSection';
import { PartnerReportsSection } from '../components/partners/profile/PartnerReportsSection';
import { PartnerScoresSection } from '../components/partners/profile/PartnerScoresSection';
import { PartnerHowItWorks } from '../components/partners/profile/PartnerHowItWorks';
import { PartnerFinalCta } from '../components/partners/profile/PartnerFinalCta';
import { PartnerResourcesSection } from '../components/partners/profile/PartnerResourcesSection';
import { PartnerStickyCta } from '../components/partners/profile/PartnerStickyCta';
export function PartnerProfile() {
  const {
    slug
  } = useParams();
  const partner = getPartner(slug);
  if (!partner) return <NotFound data-id="element-1509" data-mp="21v1uqo6o1bfd" />;
  return <PartnerProfileContent key={partner.slug} partner={partner} data-id="element-1510" data-mp="21sfrv1upzk3h" />;
}
function PartnerProfileContent({
  partner
}: {
  partner: Partner;
}) {
  const path = `/partners/${partner.slug}`;
  const {
    profile
  } = partner;
  usePageMeta({
    title: partner.seo.title,
    description: partner.seo.description,
    path,
    keywords: partner.seo.keywords,
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Partners',
        item: canonicalUrl('/partners')
      }, {
        '@type': 'ListItem',
        position: 3,
        name: partner.name,
        item: canonicalUrl(path)
      }]
    }]
  });
  useEffect(() => {
    trackEvent('partner_profile_view', {
      partner_id: partner.id,
      partner_name: partner.name
    });
  }, [partner.id, partner.name]);
  const brandVars = {
    '--pa': partner.brand.accent,
    '--pa-strong': partner.brand.accentStrong,
    '--pa-hl': partner.brand.highlight,
    '--pa-soft': partner.brand.soft
  } as React.CSSProperties;
  return <>
      <main id="main" className="bg-ink" style={brandVars} data-id="element-1511" data-mp="kkzvz92w38kg">
        <PartnerProfileHero partner={partner} data-id="element-1512" data-mp="kl9rfc6pe7xj" />
        {profile.context ? <PartnerContextSection context={profile.context} data-id="element-1513" data-mp="18aausl0oisvy" /> : null}
        {profile.reports ? <PartnerReportsSection partner={partner} reports={profile.reports} data-id="element-1514" data-mp="1da6w2chzs67t" /> : null}
        {profile.scores ? <PartnerScoresSection scores={profile.scores} partnerName={partner.name} data-id="element-1515" data-mp="zj24zysowm93" /> : null}
        {profile.howItWorks ? <PartnerHowItWorks partner={partner} how={profile.howItWorks} data-id="element-1516" data-mp="13iss9sj0ck8v" /> : null}
        {profile.resources ? <PartnerResourcesSection partner={partner} resources={profile.resources} data-id="element-1517" data-mp="1r8dkz34yqvvc" /> : null}
        <PartnerFinalCta partner={partner} data-id="element-1518" data-mp="1w0eyesdvasmy" />
        <PartnerStickyCta partner={partner} data-id="element-1519" data-mp="1w29jc6p52kr1" />
      </main>
      {/* Keeps the footer clear of the sticky bar. */}
      <div aria-hidden="true" className="h-20 bg-surface" data-id="element-1520" data-mp="3go7ma6qa1iu9" />
    </>;
}